// src/pages/dashboard/IntakeFormBuilder.js
// Route: /dashboard/intake-forms  (list)
//        /dashboard/intake-forms/new  (create)
//        /dashboard/intake-forms/:formId  (edit)

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Eye, X,
  ClipboardList, CheckSquare, AlertCircle,
} from 'lucide-react';
import { auth } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  getIntakeForms, getIntakeFormById,
  createIntakeForm, updateIntakeForm, deleteIntakeForm,
} from '../../services/intake-form-service';

// ─── Constants ──────────────────────────────────────────────────────────────

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'select', label: 'Dropdown (single select)' },
  { value: 'multiselect', label: 'Multiple Choice' },
];

const DEADLINE_OPTIONS = [
  { value: 1, label: '1 day before session' },
  { value: 2, label: '2 days before session' },
  { value: 3, label: '3 days before session' },
  { value: 7, label: '1 week before session' },
];

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  fontSize: '14px',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const makeQuestion = (order) => ({
  id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  type: 'text',
  label: '',
  required: false,
  options: [],
  placeholder: '',
  order,
});

const getServicesFromStorage = () => {
  try {
    for (const key of ['practitioner_website_draft', 'practitioner_website_published']) {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        const services = parsed?.content?.services;
        if (Array.isArray(services) && services.length > 0) return services;
      }
    }
  } catch {}
  return [];
};

// ─── QuestionPreview (read-only) ─────────────────────────────────────────────
const QuestionPreview = ({ q }) => (
  <div style={{ marginBottom: '20px' }}>
    <p style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '6px' }}>
      {q.label || <em style={{ color: '#9CA3AF' }}>Untitled question</em>}
      {q.required && <span style={{ color: '#EF4444', marginLeft: '4px' }}>*</span>}
    </p>
    {q.type === 'text' && (
      <input disabled placeholder={q.placeholder || 'Short answer'} style={{ ...inputStyle, background: '#F9FAFB', cursor: 'not-allowed', color: '#9CA3AF' }} />
    )}
    {q.type === 'textarea' && (
      <textarea disabled rows={3} placeholder={q.placeholder || 'Long answer'} style={{ ...inputStyle, resize: 'none', background: '#F9FAFB', cursor: 'not-allowed', color: '#9CA3AF' }} />
    )}
    {q.type === 'yes_no' && (
      <div style={{ display: 'flex', gap: '16px' }}>
        {['Yes', 'No'].map(v => (
          <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#374151', cursor: 'not-allowed' }}>
            <input type="radio" disabled /> {v}
          </label>
        ))}
      </div>
    )}
    {q.type === 'select' && (
      <select disabled style={{ ...inputStyle, background: '#F9FAFB', cursor: 'not-allowed', color: '#9CA3AF' }}>
        <option>{q.placeholder || 'Select an option'}</option>
        {(q.options || []).map((opt, i) => <option key={i}>{opt}</option>)}
      </select>
    )}
    {q.type === 'multiselect' && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {(q.options || []).length === 0
          ? <p style={{ fontSize: '13px', color: '#9CA3AF' }}>No options added yet</p>
          : (q.options || []).map((opt, i) => (
            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151', cursor: 'not-allowed' }}>
              <input type="checkbox" disabled /> {opt}
            </label>
          ))
        }
      </div>
    )}
  </div>
);

// ─── PreviewModal ─────────────────────────────────────────────────────────────
const PreviewModal = ({ form, onClose }) => (
  <div
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '16px' }}
    onClick={onClose}
  >
    <div
      style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto', padding: '32px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Preview — Client View</span>
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
          <X style={{ width: 20, height: 20, color: '#6B7280' }} />
        </button>
      </div>
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
        {form.title || 'Untitled Form'}
      </h2>
      {form.description && (
        <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px', lineHeight: 1.6 }}>{form.description}</p>
      )}
      <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '24px' }}>
        {(form.questions || []).length === 0
          ? <p style={{ fontSize: '14px', color: '#9CA3AF', textAlign: 'center' }}>No questions added yet</p>
          : [...(form.questions || [])].sort((a, b) => a.order - b.order).map(q => <QuestionPreview key={q.id} q={q} />)
        }
      </div>
      <button
        type="button"
        onClick={onClose}
        style={{ marginTop: '24px', width: '100%', padding: '12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
      >
        Close Preview
      </button>
    </div>
  </div>
);

// ─── DeleteConfirmModal ───────────────────────────────────────────────────────
const DeleteConfirmModal = ({ formTitle, onClose, onConfirm, deleting }) => (
  <div
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '16px' }}
    onClick={onClose}
  >
    <div
      style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '380px', padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
      onClick={e => e.stopPropagation()}
    >
      <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Delete intake form?</h2>
      <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px', lineHeight: 1.5 }}>
        Are you sure you want to delete <strong style={{ color: '#111827' }}>{formTitle}</strong>? This cannot be undone.
      </p>
      <div style={{ display: 'flex', gap: '12px' }}>
        <button type="button" onClick={onClose}
          style={{ flex: 1, padding: '10px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer' }}>
          Cancel
        </button>
        <button type="button" onClick={onConfirm} disabled={deleting}
          style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, color: '#fff', background: '#DC2626', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.6 : 1 }}>
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

// ─── QuestionEditor ────────────────────────────────────────────────────────────
const QuestionEditor = ({ q, index, total, onUpdate, onRemove, onMove }) => {
  const needsOptions = q.type === 'select' || q.type === 'multiselect';

  const addOption = () => onUpdate(q.id, 'options', [...(q.options || []), '']);
  const updateOption = (i, val) => {
    const opts = [...(q.options || [])];
    opts[i] = val;
    onUpdate(q.id, 'options', opts);
  };
  const removeOption = (i) => onUpdate(q.id, 'options', (q.options || []).filter((_, idx) => idx !== i));

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: '12px', padding: '16px', background: '#FAFAFA', marginBottom: '12px' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        {/* Move up/down */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
          <button type="button" onClick={() => onMove(index, 'up')} disabled={index === 0}
            style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '2px 4px', cursor: index === 0 ? 'not-allowed' : 'pointer', opacity: index === 0 ? 0.3 : 1 }}>
            <ChevronUp style={{ width: 12, height: 12, color: '#6B7280' }} />
          </button>
          <button type="button" onClick={() => onMove(index, 'down')} disabled={index === total - 1}
            style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '2px 4px', cursor: index === total - 1 ? 'not-allowed' : 'pointer', opacity: index === total - 1 ? 0.3 : 1 }}>
            <ChevronDown style={{ width: 12, height: 12, color: '#6B7280' }} />
          </button>
        </div>

        {/* Type select */}
        <select
          value={q.type}
          onChange={e => onUpdate(q.id, 'type', e.target.value)}
          style={{ padding: '6px 10px', fontSize: '13px', border: '1px solid #E5E7EB', borderRadius: '6px', background: '#fff', flexShrink: 0 }}
        >
          {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        {/* Required toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6B7280', cursor: 'pointer', marginLeft: 'auto', flexShrink: 0 }}>
          <input type="checkbox" checked={q.required} onChange={e => onUpdate(q.id, 'required', e.target.checked)} style={{ cursor: 'pointer' }} />
          Required
        </label>

        {/* Delete */}
        <button type="button" onClick={() => onRemove(q.id)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', flexShrink: 0 }}>
          <Trash2 style={{ width: 16, height: 16, color: '#EF4444' }} />
        </button>
      </div>

      {/* Question label */}
      <input
        type="text"
        value={q.label}
        onChange={e => onUpdate(q.id, 'label', e.target.value)}
        placeholder="Question text"
        style={{ ...inputStyle, marginBottom: '8px', background: '#fff' }}
      />

      {/* Hint text (for text/textarea/select) */}
      {(q.type === 'text' || q.type === 'textarea' || q.type === 'select') && (
        <div style={{ marginTop: '8px' }}>
          <p style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', marginBottom: '2px' }}>
            Hint text <span style={{ fontWeight: 400 }}>(optional)</span>
          </p>
          <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '6px' }}>
            Appears inside the field to guide your client (e.g., 'Describe what brings you here today')
          </p>
          <input
            type="text"
            value={q.placeholder}
            onChange={e => onUpdate(q.id, 'placeholder', e.target.value)}
            placeholder="e.g., Describe what brings you here today"
            style={{ ...inputStyle, fontSize: '13px', color: '#6B7280', background: '#fff' }}
          />
        </div>
      )}

      {/* Options editor for select/multiselect */}
      {needsOptions && (
        <div style={{ marginTop: '12px' }}>
          <p style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Options</p>
          {(q.options || []).map((opt, i) => (
            <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
              <input
                type="text"
                value={opt}
                onChange={e => updateOption(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
                style={{ ...inputStyle, flex: 1, background: '#fff' }}
              />
              <button type="button" onClick={() => removeOption(i)}
                style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '0 10px', cursor: 'pointer', flexShrink: 0 }}>
                <X style={{ width: 14, height: 14, color: '#6B7280' }} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addOption}
            style={{ padding: '6px 12px', fontSize: '13px', color: '#7c3aed', background: 'transparent', border: '1px dashed #C4B5FD', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Plus style={{ width: 14, height: 14 }} /> Add option
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const IntakeFormBuilder = () => {
  const navigate = useNavigate();
  const { formId } = useParams(); // undefined = list, 'new' = create, id = edit

  // eslint-disable-next-line no-unused-vars
  const [view, _setView] = useState(formId ? 'editor' : 'list');
  const [practitionerId, setPractitionerId] = useState(null);
  const [loading, setLoading] = useState(true);

  // List view state
  const [forms, setForms] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title }
  const [deleting, setDeleting] = useState(false);

  // Editor state
  const [editorLoading, setEditorLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([]);
  const [assignedServices, setAssignedServices] = useState([]);
  const [deadlineDays, setDeadlineDays] = useState(2);
  const [deadlineAction, setDeadlineAction] = useState('notify');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const services = getServicesFromStorage();

  // Auth + load
  useEffect(() => {
    if (!auth) { setLoading(false); return; }
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setLoading(false); } }, 3000);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !done) {
        done = true;
        clearTimeout(timeout);
        setPractitionerId(user.uid);
        if (view === 'list') {
          const data = await getIntakeForms(user.uid);
          setForms(data);
        } else if (formId && formId !== 'new') {
          setEditorLoading(true);
          const form = await getIntakeFormById(user.uid, formId);
          if (form) {
            setTitle(form.title || '');
            setDescription(form.description || '');
            setQuestions(form.questions || []);
            setAssignedServices(form.assignedServices || []);
            setDeadlineDays(form.deadlineDays ?? 2);
            setDeadlineAction(form.deadlineAction || 'notify');
          }
          setEditorLoading(false);
        }
        setLoading(false);
      }
    });
    return () => { done = true; clearTimeout(timeout); unsubscribe(); };
  }, [view, formId]);

  // ── Question helpers ──
  const addQuestion = () => {
    setQuestions(prev => [...prev, makeQuestion(prev.length)]);
  };

  const updateQuestion = (qId, field, value) => {
    setQuestions(prev => prev.map(q => q.id === qId ? { ...q, [field]: value } : q));
  };

  const removeQuestion = (qId) => {
    setQuestions(prev => {
      const filtered = prev.filter(q => q.id !== qId);
      return filtered.map((q, i) => ({ ...q, order: i }));
    });
  };

  const moveQuestion = (index, direction) => {
    setQuestions(prev => {
      const arr = [...prev];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= arr.length) return prev;
      [arr[index], arr[swapIndex]] = [arr[swapIndex], arr[index]];
      return arr.map((q, i) => ({ ...q, order: i }));
    });
  };

  const toggleService = (serviceName) => {
    setAssignedServices(prev =>
      prev.includes(serviceName)
        ? prev.filter(s => s !== serviceName)
        : [...prev, serviceName]
    );
  };

  // ── Save ──
  const handleSave = async () => {
    setSaveError('');
    if (!practitionerId) { setSaveError('Not signed in. Please refresh and try again.'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    if (!title.trim()) { setSaveError('Form title is required.'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    if (questions.length === 0) { setSaveError('Add at least one question.'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    if (questions.some(q => !q.label.trim())) { setSaveError('All questions must have text.'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }

    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        questions: questions.map((q, i) => ({ ...q, order: i })),
        assignedServices,
        deadlineDays,
        deadlineAction,
      };

      if (formId && formId !== 'new') {
        await updateIntakeForm(practitionerId, formId, payload);
      } else {
        await createIntakeForm(practitionerId, payload);
      }
      setSaveSuccess(true);
      setTimeout(() => navigate('/dashboard/intake-forms'), 800);
    } catch (err) {
      setSaveError(err.message || 'Failed to save. Please try again.');
      setSaving(false);
    }
  };

  // ── Delete ──
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteIntakeForm(practitionerId, deleteTarget.id);
      setForms(prev => prev.filter(f => f.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
    setDeleting(false);
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-3xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-48" />
            <div className="bg-white rounded-xl border border-gray-200 h-32" />
            <div className="bg-white rounded-xl border border-gray-200 h-32" />
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // LIST VIEW
  // ──────────────────────────────────────────────────────────────────────────
  if (view === 'list' && !formId) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-3xl mx-auto px-4">

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Intake Forms</h1>
              <p className="text-sm text-gray-500 mt-1">Create questionnaires to send clients before their sessions.</p>
            </div>
            <button
              onClick={() => navigate('/dashboard/intake-forms/new')}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Form
            </button>
          </div>

          {/* Empty state */}
          {forms.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">No intake forms yet</h2>
              <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
                Create intake forms to gather information from clients before their sessions. Forms are sent automatically when a client books.
              </p>
              <button
                onClick={() => navigate('/dashboard/intake-forms/new')}
                className="px-5 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors"
              >
                Create Your First Form
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {forms.map(form => (
                <div key={form.id} className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">{form.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${form.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                          {form.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{(form.questions || []).length} question{(form.questions || []).length !== 1 ? 's' : ''}</span>
                        {(form.assignedServices || []).length > 0 && (
                          <span>Assigned to: {form.assignedServices.slice(0, 2).join(', ')}{form.assignedServices.length > 2 ? ` +${form.assignedServices.length - 2} more` : ''}</span>
                        )}
                        {(form.assignedServices || []).length === 0 && (
                          <span className="text-amber-600">Not assigned to any service</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => navigate(`/dashboard/intake-forms/${form.id}`)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteTarget({ id: form.id, title: form.title })}
                        className="p-1.5 border border-gray-200 rounded-lg text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
                        aria-label="Delete form"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {deleteTarget && (
          <DeleteConfirmModal
            formTitle={deleteTarget.title}
            onClose={() => setDeleteTarget(null)}
            onConfirm={handleDeleteConfirm}
            deleting={deleting}
          />
        )}
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // EDITOR VIEW
  // ──────────────────────────────────────────────────────────────────────────
  if (editorLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-3xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-48" />
            <div className="bg-white rounded-xl border border-gray-200 h-40" />
            <div className="bg-white rounded-xl border border-gray-200 h-60" />
          </div>
        </div>
      </div>
    );
  }

  const isEditing = formId && formId !== 'new';

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-3xl mx-auto px-4">

        {/* Back */}
        <button
          type="button"
          onClick={() => navigate('/dashboard/intake-forms')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-purple-600 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Intake Forms
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEditing ? 'Edit Form' : 'Create Intake Form'}
        </h1>

        {/* Success banner */}
        {saveSuccess && (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg mb-5 text-sm text-emerald-700">
            <CheckSquare className="w-4 h-4 flex-shrink-0" />
            Form saved successfully!
          </div>
        )}

        {/* Error banner */}
        {saveError && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-5 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {saveError}
          </div>
        )}

        {/* ── Section: Title & Description ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Form Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Form Title <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g., New Client Questionnaire"
                style={inputStyle}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-gray-400 font-normal">(shown to client)</span></label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="Brief introduction shown at the top of the form…"
                style={{ ...inputStyle, resize: 'none' }}
              />
            </div>
          </div>
        </div>

        {/* ── Section: Questions ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-900">Questions</h2>
            <span className="text-xs text-gray-500">{questions.length} question{questions.length !== 1 ? 's' : ''}</span>
          </div>

          {questions.length === 0 && (
            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg mb-4">
              <p className="text-sm text-gray-500">No questions yet. Add your first question below.</p>
            </div>
          )}

          {questions.map((q, i) => (
            <QuestionEditor
              key={q.id}
              q={q}
              index={i}
              total={questions.length}
              onUpdate={updateQuestion}
              onRemove={removeQuestion}
              onMove={moveQuestion}
            />
          ))}

          <button
            type="button"
            onClick={addQuestion}
            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-purple-400 hover:text-purple-600 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Question
          </button>
        </div>

        {/* ── Section: Assign to Services ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">Assign to Services</h2>
          <p className="text-xs text-gray-500 mb-4">
            When a client books one of these services, this form is automatically sent.
          </p>

          {services.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                No services found. Add services in the{' '}
                <button
                  type="button"
                  onClick={() => navigate('/site-editor')}
                  className="font-medium underline hover:no-underline"
                >
                  Site Editor
                </button>{' '}
                to assign this form.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {services.map((svc, i) => {
                const svcName = svc.name || svc.title || `Service ${i + 1}`;
                const checked = assignedServices.includes(svcName);
                return (
                  <label key={i} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleService(svcName)}
                      className="w-4 h-4 accent-purple-600 cursor-pointer"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{svcName}</p>
                      {(svc.duration || svc.price != null) && (
                        <p className="text-xs text-gray-500">
                          {svc.duration ? `${svc.duration} min` : ''}
                          {svc.duration && svc.price != null ? ' · ' : ''}
                          {svc.price != null ? `$${svc.price}` : ''}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Section: Deadline ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Deadline Settings</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Form due</label>
              <select
                value={deadlineDays}
                onChange={e => setDeadlineDays(Number(e.target.value))}
                style={{ ...inputStyle, width: 'auto', minWidth: '240px' }}
              >
                {DEADLINE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">If not completed by deadline</p>
              <div className="space-y-2">
                {[
                  { value: 'notify', label: 'Notify me to follow up' },
                  { value: 'cancel', label: 'Auto-cancel the booking' },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="deadlineAction"
                      value={opt.value}
                      checked={deadlineAction === opt.value}
                      onChange={() => setDeadlineAction(opt.value)}
                      className="w-4 h-4 accent-purple-600"
                    />
                    <span className="text-sm text-gray-700">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Action bar ── */}
        <div className="flex items-center justify-between gap-4 pb-10">
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Preview Form
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/dashboard/intake-forms')}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Save Form'}
            </button>
          </div>
        </div>
      </div>

      {showPreview && (
        <PreviewModal
          form={{ title, description, questions }}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default IntakeFormBuilder;
